<template>
  <div>
    <div class="header">
      <div class="header__item" style="margin-top: 20px">
        <CustomSelect
          :options="this.residencyOptions"
          label="ЖК"
          optionValueKey="resident_id"
          optionLabelKey="resident_name"
          :value="this.filters.resident_id"
          @change="(value) => onChange('resident_id', value)"
        />
      </div>
      <div class="header__item">
        <CustomSelect
          :options="this.entranceOptions"
          label="Подъезд"
          optionValueKey="entrance"
          optionLabelKey="entrance"
          :value="this.filters.entrance"
          @change="(value) => onChange('entrance', value)"
        />
      </div>
      <q-btn
        type="submit"
        @click="this.getData"
        :loading="this.isFetching"
        :disable="this.deleting"
        size="sm"
        no-wrap
        color="primary"
      >
        <q-icon name="search" />
        Поиск
      </q-btn>
    </div>
    <CustomTable
      title="Список МОП"
      :rowsData="this.rowsData"
      :columns="this.flatsListColumns"
      :loading="isFetching"
      :show-search="true"
      :custom-body="true"
      :add-btn="true"
      :on-add-click="this.handleAddClick"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.flat_id }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <span>{{ props.row.entrance }}</span>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <span>{{ props.row.floor }}</span>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CheckOrClose :value="props.row.is_active" />
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CustomIcon
              @click="() => this.handleCloneClick(props.row)"
              icon="clone"
              small
            />
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CustomIcon
              @click="() => this.handleUpdateClick(props.row)"
              icon="edit"
              green
              small
            />
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <CustomIcon
              icon="trash"
              :approve-mode="true"
              :loading="!!this.deleteLoading[props.row.flat_id]"
              :approve="() => this.onDeleteSubmit(props.row.flat_id)"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import { appActions } from "@/services/store/modules/app_module/app_actions";
import { MODAL_NAMES } from "@/constants";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

import CustomIcon from "@/components/common/CustomIcon";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import PopupConfirm from "@/components/common/PopupConfirm";
import CheckOrClose from "@/components/common/CheckOrClose";
import {
  getResidencyOptions,
  getEntranceOptions,
  flatsListColumns,
  updateFlat,
  cloneFlat,
  deleteFlat,
  getFlatsList,
} from "@/views/FlatList/services";

export default {
  name: "FlatList",
  components: {
    CustomInput,
    CustomIcon,
    CustomTableCell,
    CustomTable,
    CustomSelect,
    PopupConfirm,
    CheckOrClose,
  },
  data() {
    return {
      residencyOptions: [],
      entranceOptions: [],
      rowsData: [],
      flatsListColumns,
      filters: {
        resident_id: "",
        entrance: "",
      },
      isFetching: false,
      deleteLoading: {},
      deleting: false,
    };
  },
  mounted() {
    this.getResidencyOptions();
  },
  methods: {
    async getResidencyOptions() {
      const res = await getResidencyOptions();
      this.residencyOptions = res || [];
    },
    async onChange(key, value) {
      this.filters[key] = value;
      if (key === "resident_id" && value !== "") {
        const res = await getEntranceOptions(this.filters.resident_id);
        this.entranceOptions = res || [];
        if (!this.entranceOptions.length) {
          this.filters.entrance = "";
        }
      }
    },
    async getData() {
      this.isFetching = true;
      const res = await getFlatsList(this.filters);
      this.isFetching = false;
      if (!res) return;
      this.rowsData = res;
    },
    async handleAddClick() {
      if (!this.filters.resident_id) {
        this.showErrorSnackbar("Выберите ЖК");
        return;
      }
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.FLAT_COMMON_AREA_UPD, {
          onSubmit: this.onAddSubmit,
          title: "Добавить МОП",
          resident_id: this.filters.resident_id,
          entrance: this.filters.entrance,
          entranceOptions: this.entranceOptions,
        })
      );
    },
    async handleUpdateClick(row) {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.FLAT_COMMON_AREA_UPD, {
          onSubmit: (body) => this.onUpdateSubmit(body),
          title: "Изменить МОП",
          body: row,
          resident_id: this.filters.resident_id,
          entranceOptions: this.entranceOptions,
        })
      );
    },
    async handleCloneClick(row) {
      this.$store.commit(
        appActions.showModal(MODAL_NAMES.FLAT_CLONE, {
          onSubmit: (body) => this.onCloneSubmit(body),
          title: "Дублировать МОП",
          body: row,
          resident_id: this.filters.resident_id,
        })
      );
    },
    async onAddSubmit(body) {
      const res = await updateFlat(body);
      if (!res) return;
      this.getData();
      this.showSucceedSnackbar();
      this.$store.commit(appActions.closeModal());
    },
    async onUpdateSubmit(body) {
      const res = await updateFlat(body);
      if (!res) return;
      this.getData();
      this.showSucceedSnackbar();
      this.$store.commit(appActions.closeModal());
    },
    async onCloneSubmit(body) {
      const res = await cloneFlat(body);
      if (!res) return;
      this.getData();
      this.showSucceedSnackbar();
      this.$store.commit(appActions.closeModal());
    },
    async onDeleteSubmit(flat_id) {
      if (this.deleteLoading[flat_id]) return;
      this.deleting = true;
      this.deleteLoading = { ...this.deleteLoading, [flat_id]: true };
      const res = await deleteFlat(flat_id);
      this.deleteLoading = { ...this.deleteLoading, [flat_id]: false };
      this.deleting = false;
      if (!res) return;
      this.rowsData = this.rowsData.filter(row => row.flat_id !== flat_id);
      this.showSucceedSnackbar();
    },
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style scoped lang="scss">
.header {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 25px;
  &__item {
    width: 200px;
  }
  @media (max-width: 400px) {
    &__item {
      width: 100%;
    }
  }
}
</style>
